
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { AuthAction, AuthGetter } from '@/libs/auth/models/auth-state';
import { authStore } from '@/libs/auth/+store/store';
import { applicationUserStore } from '@/libs/user-settings/+state/store';

import { TooltipPlugin } from '@syncfusion/ej2-vue-popups';
import { ApplicationUserModel, PermissionTypeEnum, TenantModel } from '@/libs/Api';
import { tenantsStore } from '@/libs/tenants/+state/store';
import { AuthenticationType } from '@/libs/auth/models/auth-type';
import { PublicClientApplication } from '@azure/msal-browser';
import NotificationSubscription from '@/libs/common/components-smart/NotificationSubscription.vue';
import { getDataDetail } from '@microsoft/signalr/dist/esm/Utils';
Vue.use(TooltipPlugin);

@Component({
    methods: { getDataDetail },
    components: { NotificationSubscription },
})
export default class SidebarMenu extends Vue {
    enableDock = true;
    dockSize = '72px';
    width = '220px';
    position = 'Left';
    fields = { tooltip: 'text' };
    linkGroupId: string | null = null;
    mobileNavCollapsed = false;
    tenantMenu = false;
    selectedTenant: string | null = null;

    @Prop() tenantId!: string;

    get isSharedMode(): boolean {
        return authStore.useGetter(AuthGetter.GetSharedMode);
    }

    get isNotificationMode(): boolean {
        return authStore.useGetter(AuthGetter.GetNotificationMode);
    }

    get isMobile(): boolean {
        return this.$vuetify.breakpoint.width < 991;
    }

    protected get BlobStorage(): string {
        return (window as any).BlobStorage ?? 'https://idominoams.blob.core.windows.net/ams-public';
    }

    getDataList(): Array<any> {
        const tenantId = authStore.useGetter(AuthGetter.GetTenant);
        const useTpm = (window as any).UseTpm;
        if (tenantId && tenantId.length > 0) {
            const onlyDashboard = authStore.useGetter(AuthGetter.GetOnlyDashboard);
            const onlyDashboardAndCalendar = authStore.useGetter(AuthGetter.GetOnlyDashboardAndCalendar);
            const disableDashboard = authStore.useGetter(AuthGetter.GetDisableDashboard);
            if (onlyDashboardAndCalendar) {
                return [
                    {
                        text: this.$i18n.t('menu.dashboard'),
                        to: { name: 'Dashboard', params: { tenantId: tenantId } },
                        icon: 'dashboard.svg',
                    },
                    {
                        text: this.$i18n.t('menu.tasks'),
                        to: { name: 'WorkTasks', params: { tenantId: tenantId } },
                        icon: 'calendar.svg',
                        permissions: [PermissionTypeEnum.Response, PermissionTypeEnum.PerformAudit, PermissionTypeEnum.Plan],
                    },
                ].filter((x) => this.hasPermissions(x.permissions ?? []) || !x.permissions || x.permissions.length == 0);
            } else if (onlyDashboard) {
                return [
                    {
                        text: this.$i18n.t('menu.dashboard'),
                        to: { name: 'Dashboard', params: { tenantId: tenantId } },
                        icon: 'dashboard.svg',
                    },
                ];
            }
            let items: {
                text: any;
                to: any;
                icon: any;
                permissions?: Array<PermissionTypeEnum>;
            }[] = !disableDashboard
                ? [
                      {
                          text: this.$i18n.t('menu.dashboard'),
                          to: { name: 'Dashboard', params: { tenantId: tenantId } },
                          icon: 'dashboard.svg',
                      },
                  ]
                : [];
            items = [
                ...items,
                ...[
                    {
                        text: this.$i18n.t('menu.audit'),
                        to: { name: 'Audits', params: { tenantId: tenantId } },
                        icon: 'audits.svg',
                        permissions: [
                            PermissionTypeEnum.Audits,
                            PermissionTypeEnum.EditAllAudits,
                            PermissionTypeEnum.EditOwnedAudit,
                            PermissionTypeEnum.PerformAudit,
                        ],
                    },
                ],
            ];
            if (useTpm) {
                items = [
                    ...items,
                    ...[
                        {
                            text: this.$i18n.t('menu.shiftWork'),
                            to: { name: 'ShiftWork', params: { tenantId: tenantId } },
                            icon: 'shiftWork.svg',
                            permissions: [PermissionTypeEnum.ShiftManagement],
                        },
                        {
                            text: this.$i18n.t('menu.shifts'),
                            to: { name: 'Shifts', params: { tenantId: tenantId } },
                            icon: 'shifts.svg',
                            permissions: [PermissionTypeEnum.ShiftManagement],
                        },
                        {
                            text: this.$i18n.t('menu.shiftStations'),
                            to: { name: 'ShiftStations', params: { tenantId: tenantId } },
                            icon: 'shiftStations.svg',
                            permissions: [PermissionTypeEnum.ShiftManagement],
                        },
                        {
                            text: this.$i18n.t('menu.shiftWorkList'),
                            to: { name: 'ShiftWorkList', params: { tenantId: tenantId } },
                            icon: 'shiftWorks.svg',
                            permissions: [PermissionTypeEnum.ShiftWork, PermissionTypeEnum.ShiftWorkLimited],
                        },
                        {
                            text: this.$i18n.t('menu.shiftReport'),
                            to: { name: 'ShiftReport', params: { tenantId: tenantId } },
                            icon: 'shiftReport.svg',
                            permissions: [PermissionTypeEnum.ShiftOverview],
                        },
                    ],
                ];
            }
            items = [
                ...items,
                ...[
                    {
                        text: this.$i18n.t('menu.tasks'),
                        to: { name: 'WorkTasks', params: { tenantId: tenantId } },
                        icon: 'calendar.svg',
                        permissions: [PermissionTypeEnum.Response, PermissionTypeEnum.PerformAudit, PermissionTypeEnum.Plan],
                    },
                ],
            ];
            if (useTpm) {
                items = [
                    ...items,
                    ...[
                        {
                            text: this.$i18n.t('menu.issuesSolving'),
                            to: { name: 'IssuesSolving', params: { tenantId: tenantId } },
                            icon: 'issuesSolving.svg',
                            permissions: [PermissionTypeEnum.ReportIssueSolving, PermissionTypeEnum.PerformAudit],
                        },
                    ],
                ];
            }
            items = [
                ...items,
                ...[
                    {
                        text: this.$i18n.t('menu.productInspection'),
                        to: { name: 'ProductInspections', params: { tenantId: tenantId } },
                        icon: 'productInspection.svg',
                        permissions: [PermissionTypeEnum.PerformProductInspection],
                    },
                    {
                        text: this.$i18n.t('menu.responses'),
                        to: { name: 'Responses', params: { tenantId: tenantId } },
                        icon: 'responses.svg',
                        permissions: [
                            PermissionTypeEnum.PerformAudit,
                            PermissionTypeEnum.Response,
                            PermissionTypeEnum.MistakeCorrection,
                            PermissionTypeEnum.ResponseCorrection,
                        ],
                    },
                ],
            ];
            if (useTpm) {
                items = [
                    ...items,
                    ...[
                        {
                            text: this.$i18n.t('menu.reportedIssueSolving'),
                            to: { name: 'ReportedIssueSolving', params: { tenantId: tenantId } },
                            icon: 'reportedIssueSolving.svg',
                            permissions: [
                                PermissionTypeEnum.PerformAudit,
                                PermissionTypeEnum.Response,
                                PermissionTypeEnum.MistakeCorrection,
                            ],
                        },
                        {
                            text: this.$i18n.t('menu.reportedMaintenance'),
                            to: { name: 'ReportedMaintenance', params: { tenantId: tenantId } },
                            icon: 'reportedMaintenance.svg',
                            permissions: [
                                PermissionTypeEnum.PerformAudit,
                                PermissionTypeEnum.Response,
                                PermissionTypeEnum.MistakeCorrection,
                            ],
                        },
                        {
                            text: this.$i18n.t('menu.reportedChecklists'),
                            to: { name: 'ReportedChecklists', params: { tenantId: tenantId } },
                            icon: 'reportedChecklists.svg',
                            permissions: [
                                PermissionTypeEnum.PerformAudit,
                                PermissionTypeEnum.Response,
                                PermissionTypeEnum.MistakeCorrection,
                            ],
                        },
                    ],
                ];
            }
            items = [
                ...items,
                ...[
                    {
                        text: this.$i18n.t('menu.reportedProductInspection'),
                        to: { name: 'ReportedProductInspection', params: { tenantId: tenantId } },
                        icon: 'reportedProductInspection.svg',
                        permissions: [PermissionTypeEnum.ProductInspections, PermissionTypeEnum.MistakeCorrection],
                    },
                    {
                        text: this.$i18n.t('menu.unfinishedResponses'),
                        to: { name: 'Unanswered', params: { tenantId: tenantId } },
                        icon: 'unanswered-responses.svg',
                        permissions: [PermissionTypeEnum.PerformAudit, PermissionTypeEnum.Response],
                    },
                ],
            ];
            if (useTpm) {
                items = [
                    ...items,
                    ...[
                        {
                            text: this.$i18n.t('menu.waitingMaintenance'),
                            to: { name: 'WaitingMaintenance', params: { tenantId: tenantId } },
                            icon: 'waitingMaintenance.svg',
                            permissions: [PermissionTypeEnum.PerformAudit, PermissionTypeEnum.Response],
                        },
                    ],
                ];
            }
            items = [
                ...items,
                ...[
                    {
                        text: this.$i18n.t('menu.issues'),
                        to: { name: 'Issues', params: { tenantId: tenantId } },
                        icon: 'issues.svg',
                        permissions: [PermissionTypeEnum.Findings, PermissionTypeEnum.FindingManager],
                    },
                    {
                        text: this.$i18n.t('menu.findings'),
                        to: { name: 'Findings', params: { tenantId: tenantId } },
                        icon: 'findings.svg',
                        permissions: [PermissionTypeEnum.Findings, PermissionTypeEnum.FindingManager],
                    },
                    {
                        text: this.$i18n.t('menu.solvedIssues'),
                        to: { name: 'Measures', params: { tenantId: tenantId } },
                        icon: 'measure.svg',
                        permissions: [
                            PermissionTypeEnum.Measures,
                            PermissionTypeEnum.MeasuresAllUsers,
                            PermissionTypeEnum.MeasureView,
                        ],
                    },
                    {
                        text: this.$i18n.t('menu.verification'),
                        to: { name: 'Verification', params: { tenantId: tenantId } },
                        icon: 'resources.svg',
                        permissions: [PermissionTypeEnum.MeasureVerification, PermissionTypeEnum.MeasureView],
                    },
                    {
                        text: this.$i18n.t('menu.review'),
                        to: { name: 'Review', params: { tenantId: tenantId } },
                        icon: 'tasks.svg',
                        permissions: [PermissionTypeEnum.PlanItemReview],
                    },
                ],
            ];
            if (useTpm) {
                items = [
                    ...items,
                    ...[
                        {
                            text: this.$i18n.t('menu.tpmAnalytics'),
                            to: { name: 'TpmAnalyticsDashboard', params: { tenantId: tenantId } },
                            icon: 'panelDashboards.svg',
                        },
                        /*{
                            text: this.$i18n.t('menu.machineTimeline'),
                            to: { name: 'MachineTimeline', params: { tenantId: tenantId } },
                            icon: 'panelDashboards.svg',
                        },*/
                        {
                            text: this.$i18n.t('menu.panelDashboards'),
                            to: { name: 'PanelDashboards', params: { tenantId: tenantId } },
                            icon: 'panelDashboards.svg',
                            permissions: [PermissionTypeEnum.PanelDashboardEdit, PermissionTypeEnum.PanelDashboardView],
                        },
                    ],
                ];
            }
            items = [
                ...items,
                ...[
                    {
                        text: this.$i18n.t('menu.reports'),
                        to: { name: 'ReportByQuestion', params: { tenantId: tenantId } },
                        icon: 'reports.svg',
                        permissions: [
                            PermissionTypeEnum.Response,
                            PermissionTypeEnum.Audits,
                            PermissionTypeEnum.PerformProductInspection,
                        ],
                    },
                    /*{
                        text: 'ListLayout Test',
                        to: { name: 'ListLayoutTest', params: { tenantId: tenantId } },
                        icon: 'dashboard.svg',
                    },*/
                ],
            ];
            return items.filter((x) => this.hasPermissions(x.permissions ?? []) || !x.permissions || x.permissions.length == 0);
        } else {
            return [];
        }
    }

    hasMore(): boolean {
        return 220 + this.getDataList().length * 56 > this.$vuetify.breakpoint.height;
    }

    get userInfo(): ApplicationUserModel {
        return applicationUserStore.useGetter(CrudGetter.Detail);
    }

    get tenants(): Array<TenantModel> {
        return tenantsStore.useGetter(CrudGetter.Data);
    }

    get tenant(): TenantModel {
        return tenantsStore.useGetter(CrudGetter.Detail);
    }

    get useTpm(): boolean {
        return (window as any).UseTpm ?? false;
    }

    @Watch('$route')
    routeChanged() {
        this.detectTenantMenu();
        this.mobileNavCollapsed = false;
    }

    @Watch('tenantId')
    tenantIdChanged(v: string, p: string) {
        if (v != null && v !== p && v != this.$route.params.tenantId) {
            this.selectedTenant = v;
        }
    }

    @Watch('selectedTenant')
    selectedTenantChanged(v: string, p: string) {
        if (v != null && v !== p && v != this.$route.params.tenantId) {
            console.log('set tenant: ' + v);
            authStore.dispatch(AuthAction.SetTenant, v);
        }
    }

    mounted() {
        this.detectTenantMenu();
        if (this.tenantId) this.selectedTenant = this.tenantId;
    }

    private signout() {
        authStore.dispatch(AuthAction.SignOut);
        const authenticationType = authStore.useGetter(AuthGetter.GetAuthenticationType) as AuthenticationType;
        if (authenticationType == AuthenticationType.AzureActiveDirectory) {
            const azureTenantId = authStore.useGetter(AuthGetter.GetAzureTenantId);
            const azureAppId = authStore.useGetter(AuthGetter.GetAzureAppId);
            const msalConfig = {
                auth: {
                    clientId: azureAppId,
                    authority: 'https://login.microsoftonline.com/' + azureTenantId,
                },
            };
            const msalInstance = new PublicClientApplication(msalConfig);
            msalInstance.handleRedirectPromise();
            msalInstance.logoutPopup();
        }
    }

    private detectTenantMenu() {
        const tenantId = authStore.useGetter(AuthGetter.GetTenant);
        //this.tenantMenu = this.$router.currentRoute.meta?.tenant != false;
        this.tenantMenu = tenantId && tenantId.length > 0;
    }

    hasPermissions(permissionTypes: PermissionTypeEnum[]) {
        if (!permissionTypes) return true;
        const permissions = authStore.useGetter(AuthGetter.GetPermissions) as PermissionTypeEnum[];
        return permissions.any((x) => permissionTypes.any((p) => p == x));
    }

    private toUserSettings() {
        this.$router.push({ name: 'UserSettings' });
    }

    protected onQuickLoginClick() {
        this.$emit('quick-login');
    }

    protected onActivateQuickLoginClick() {
        this.$emit('active-quick-login-click');
    }

    protected onDeactivateQuickLoginClick() {
        this.$emit('deactive-quick-login-click');
    }

    protected closeMenu() {
        this.mobileNavCollapsed = false;
    }
}
